import { GroupWidgetPort, PortType, WidgetType } from '@kemu-io/kemu-core/types';
import { BrowserJsPlumbInstance } from '@jsplumb/community/types/dom/browser-jsplumb-instance.js';
import { Endpoint } from '@jsplumb/community/types/core';
import classNames from 'classnames';
import { createWidgetPortIdentifier } from '@kemu-io/kemu-core/common/utils';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { DataType } from '@kemu-io/hs-types';
import { PortDescription, WidgetPortWithId } from '@components/gates';
import { getEndpointSetting } from '@common/jsPlumb/settings';
import { PortLocation } from '@src/types/canvas_t';
import {
  CUSTOM_WIDGET_PORT_COLOR_FLAG, CUSTOM_WIDGET_PORT_COLOR_PREFIX, DISABLED_WIDGET_CLASS,
  GATE_PORT_CLASS, KEMU_PORT_CLASS, OFFLINE_SERVICE_CLASS, SOURCE_PORT_CLASS,
  TARGET_PORT_CLASS, TRIGGER_PORT_CLASS, VIRTUAL_PORT_CLASS, WIDGET_PORT_RADIUS
} from '@common/constants';
import { getCategoryFromType } from '@common/widgetCategories';
import { KemuEndpointData } from '@src/types/core_t';
import { generateDomId } from '@common/utils';


/** Builds valid port settings depending on the type */
const getEndpointConfig = (isTarget: boolean, id: string, customPosition: PortLocation) => {
	// top (LR), left (TB), (dx, dy) <== direction of curve
	const portSettings = getEndpointSetting(isTarget, customPosition, WIDGET_PORT_RADIUS);
	portSettings.portId = id;
	portSettings.parameters = {
		isTarget,
	};

	portSettings.uuid = id;
	return portSettings;
};

const buildPortClassesForPort = (config: {
	plumbPort: Endpoint,
	portInfo: WidgetPortWithId,
	widgetType: WidgetType,
	widgetColor?: string,
	widgetDisabled?: boolean,
	serviceOffline?: boolean,
}) => {
	const { plumbPort, portInfo, widgetType, widgetColor, widgetDisabled, serviceOffline } = config;

	if (plumbPort) {
		const portParameters = plumbPort.getParameters();
		// Update the json shape in case it changed
		plumbPort.setParameter('jsonShape', portInfo.jsonShape);

		// Remove all existing classes
		plumbPort.removeClass(plumbPort.endpoint.classes[0]);

		// Build a new list of classes
		const portTypes = (Array.isArray(portInfo.type) ? portInfo.type : [portInfo.type]).map((arrType) => `type-${arrType}`);
		const cssClasses = classNames(
			portParameters.isTarget ? TARGET_PORT_CLASS : SOURCE_PORT_CLASS,
			'port',
			GATE_PORT_CLASS,
			`ctgry-${getCategoryFromType(widgetType)}`,
			...portTypes,
			{
				// 'original-event': gateInfo.returnOriginalEvent,
				[DISABLED_WIDGET_CLASS]: widgetDisabled,
				[OFFLINE_SERVICE_CLASS]: serviceOffline,
				[CUSTOM_WIDGET_PORT_COLOR_FLAG]: widgetColor,
				[`${CUSTOM_WIDGET_PORT_COLOR_PREFIX}${widgetColor?.replace('#', '')}`]: widgetColor,
				[TRIGGER_PORT_CLASS]: portInfo.triggerPort,
			}
		);

		// This is used by the port debugging panel
		const endpointData: KemuEndpointData = {
			type: portInfo.type,
			name: portInfo.name,
			label: portInfo.label,
			id: portInfo.id,
			jsonShape: portInfo.jsonShape
		};

		plumbPort.setData(endpointData);

		plumbPort.addClass(cssClasses);
	}
};

const buildWidgetPortsInfo = (portType: 'input' | 'output', widgetId: string, port: PortDescription): WidgetPortWithId => {
	const portId = createWidgetPortIdentifier(widgetId, portType, port.name);
	return {
		id: portId,
		name: port.name,
		type: port.type,
		jsonShape: port.jsonShape,
		triggerPort: port.triggerPort,
	};
};

const buildVirtualPort = (config: {
	widgetId: string,
	recipeId: string,
	plumbInstance: BrowserJsPlumbInstance,
  port: GroupWidgetPort,
	portName: string,
  portType: 'innerOutput' | 'innerInput',
  isOutput: boolean,
  portDataType: DataType[],
}): ({
  addedPort: Endpoint,
  /** the id of the DOM element that represents the port */
  domElementId: string
} | null) => {
  const { widgetId, recipeId, plumbInstance, portType, isOutput, port } = config;
  const containerDomId = generateDomId(recipeId, DEFAULT_THING_ID, widgetId, port.name);
  const containerEl = document.getElementById(containerDomId);

  if (containerEl && plumbInstance) {
    const portId = createWidgetPortIdentifier(widgetId, portType, port.name);
    const sourcePortSettings = getEndpointConfig(!isOutput, portId, isOutput ? 'Right' : 'Left');
    const portTypes = (Array.isArray(port.type) ? port.type : [port.type]).map((arrType) => `type-${arrType}`);
    sourcePortSettings.cssClass = classNames(
      sourcePortSettings.cssClass,
      isOutput ? SOURCE_PORT_CLASS : TARGET_PORT_CLASS,
      VIRTUAL_PORT_CLASS,
      KEMU_PORT_CLASS,
      GATE_PORT_CLASS,
      `ctgry-${getCategoryFromType(WidgetType.widgetGroup)}`,
      ...portTypes,
    );

    // This is used by the port debugging panel
    const endpointData: KemuEndpointData = {
      type: port.type,
      name: port.name,
      label: port.label,
      id: portId,
      jsonShape: port.jsonShape
    };

    const addedPort = plumbInstance.addEndpoint(containerEl, { ...sourcePortSettings });
    addedPort.setData(endpointData);

    // Make the virtual port element manageable for selection
    plumbInstance.manage(containerEl);

    return { addedPort, domElementId: containerDomId };
  }

  return null;
};


/**
 * Adds ports to the given gate element
 */
const buildPorts = (config: {
	gateId: string,
	recipeId: string,
	plumbInstance: BrowserJsPlumbInstance,
	inputPorts: PortDescription[],
	outputPorts: PortDescription[],
	widgetType: WidgetType,
  portName?: string,
	widgetColor?: string,
	widgetDisabled?: boolean,
	serviceOffline?: boolean,
	// gateEl: HTMLElement
}) => {
	const { gateId, recipeId, plumbInstance, inputPorts, outputPorts, widgetType, portName, widgetColor, widgetDisabled, serviceOffline } = config;
	const gateEl = document.getElementById(generateDomId(recipeId, DEFAULT_THING_ID, gateId, portName));
	const hasPorts = inputPorts.length || outputPorts.length;

	if (!hasPorts) {
		// Without adding ports, the element is not draggable, 
		// here we make sure it is by making it a managed element
		// Only manage if the element exists (might not be rendered yet when grouping)
		if (gateEl) {
			plumbInstance.manage(gateEl);
		}
		return;
	}

	// plumbInstance.batch(() => {
		inputPorts.map((port, index) => {
			// const portId = getUniqueWidgetPortId(index, 'input', gateId, port.name);
			const portId = createWidgetPortIdentifier(gateId, 'input', port.name);
			if (gateEl) {
				const targetPortSettings = getEndpointConfig(true, portId, port.position);
				if (widgetColor) {
					targetPortSettings.paintStyle = {
						...targetPortSettings.paintStyle,
						stroke: widgetColor,
					};
				}

				const addedPort = plumbInstance.addEndpoint(gateEl, { ...targetPortSettings });
				buildPortClassesForPort({
					plumbPort: addedPort,
					portInfo: buildWidgetPortsInfo('input', gateId, port),
					widgetType: widgetType,
					widgetColor,
					widgetDisabled,
					serviceOffline,
				});
			}
		});

		outputPorts.map((port, index) => {
			// const portId = getUniqueWidgetPortId(index, 'output', gateId, port.name);
			const portId = createWidgetPortIdentifier(gateId, 'output', port.name);
			if (gateEl) {
				const sourcePortSettings = getEndpointConfig(false, portId, port.position);
				if (widgetColor) {
					sourcePortSettings.paintStyle = {
						...sourcePortSettings.paintStyle,
						stroke: widgetColor,
					};
				}

				const existingPorts = plumbInstance.getEndpoints(gateEl);
				const alreadyAdded = existingPorts.some(port => port.portId === portId);
				if (alreadyAdded) {
					return;
				}
				const addedPort = plumbInstance.addEndpoint(gateEl, { ...sourcePortSettings });
				buildPortClassesForPort({
					plumbPort: addedPort,
					portInfo: buildWidgetPortsInfo('input', gateId, port),
					widgetType: widgetType,
					widgetColor,
					widgetDisabled,
					serviceOffline,
				});
			}
		});
	// });
};

const portHelpers = {
	buildPorts,
	buildWidgetPortsInfo,
	buildPortClassesForPort,
	getEndpointConfig,
	buildVirtualPort,
};

export default portHelpers;
